/**
 * WebSocket Hook for Real-time Updates
 *
 * Provides real-time communication with the server for:
 * - Payment notifications
 * - Attendance updates
 * - Application status changes
 * - System notifications
 */

import { useEffect, useCallback, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { useQueryClient } from "@tanstack/react-query";
import { TokenStorage } from "@/api/client";
import { showInfo, showSuccess, showError } from "@/utils/toast";

// WebSocket event types (must match backend events.py)
export enum WebSocketEvent {
  // Connection events
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",

  // Payment events
  PAYMENT_RECEIVED = "payment.received",
  PAYMENT_FAILED = "payment.failed",
  PAYMENT_REFUNDED = "payment.refunded",
  AUTOPAY_PROCESSED = "autopay.processed",
  AUTOPAY_FAILED = "autopay.failed",

  // Invoice events
  INVOICE_GENERATED = "invoice.generated",
  INVOICE_SENT = "invoice.sent",
  INVOICE_PAID = "invoice.paid",
  INVOICE_OVERDUE = "invoice.overdue",

  // Attendance events
  ATTENDANCE_RECORDED = "attendance.recorded",
  ATTENDANCE_UPDATED = "attendance.updated",
  SESSION_STARTED = "session.started",
  SESSION_COMPLETED = "session.completed",

  // Application events
  APPLICATION_SUBMITTED = "application.submitted",
  APPLICATION_APPROVED = "application.approved",
  APPLICATION_DENIED = "application.denied",

  // Enrollment events
  ENROLLMENT_CREATED = "enrollment.created",
  ENROLLMENT_WITHDRAWN = "enrollment.withdrawn",

  // Notification events
  NOTIFICATION_NEW = "notification.new",
  NOTIFICATION_READ = "notification.read",

  // Discount events
  DISCOUNT_PENDING_APPROVAL = "discount.pending_approval",
  DISCOUNT_APPROVED = "discount.approved",
  DISCOUNT_DENIED = "discount.denied",

  // System events
  SYSTEM_ANNOUNCEMENT = "system.announcement",
  DATA_SYNC = "data.sync",

  // Academic events (Phase 1B / Phase 2 / Phase 3) — must match backend
  // commons/realtime.py::EventType.
  GRADE_POSTED = "grade.posted",
  BEHAVIOR_LOGGED = "behavior.logged",
  REPORT_CARD_GENERATED = "reportcard.generated",
  MESSAGE_NEW = "message.new",
}

export interface WebSocketMessage<T = unknown> {
  event: string;
  data: T;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

type EventHandler<T = unknown> = (data: T, message: WebSocketMessage<T>) => void;

interface UseWebSocketOptions {
  /** Enable automatic reconnection */
  autoReconnect?: boolean;
  /** Reconnection delay in ms */
  reconnectDelay?: number;
  /** Maximum reconnection attempts */
  maxReconnectAttempts?: number;
  /** Enable debug logging */
  debug?: boolean;
}

interface UseWebSocketReturn {
  /** Current connection status */
  isConnected: boolean;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Subscribe to a specific event type */
  subscribe: <T = unknown>(event: WebSocketEvent | string, handler: EventHandler<T>) => () => void;
  /** Send a message (for ping/pong, etc.) */
  send: (message: Record<string, unknown>) => void;
}

// Get WebSocket URL from environment
const getWebSocketUrl = (): string => {
  const wsUrl = import.meta.env.VITE_WEBSOCKET_URL;
  if (wsUrl) return wsUrl;

  // Fallback: derive from API URL
  const apiUrl = import.meta.env.VITE_API_URL || "";
  if (apiUrl.includes("localhost")) {
    return "ws://localhost:3001/ws"; // Local dev server
  }

  // Production: replace https with wss
  return apiUrl.replace("https://", "wss://").replace("/api", "/ws");
};

// ========================================
// Singleton WebSocket Manager
// ========================================
// Ensures only ONE WebSocket connection exists across all hook instances

interface WebSocketManager {
  ws: WebSocket | null;
  handlers: Map<string, Set<EventHandler>>;
  connectionCount: number;
  reconnectAttempts: number;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  isConnecting: boolean;
}

const wsManager: WebSocketManager = {
  ws: null,
  handlers: new Map(),
  connectionCount: 0,
  reconnectAttempts: 0,
  reconnectTimeout: null,
  isConnecting: false,
};

/**
 * Hook for WebSocket real-time communication
 *
 * Uses a singleton WebSocket connection shared across all hook instances
 * to prevent duplicate messages from multiple connections.
 *
 * @example
 * const { isConnected, subscribe } = useWebSocket();
 *
 * useEffect(() => {
 *   const unsubscribe = subscribe(WebSocketEvent.PAYMENT_RECEIVED, (data) => {
 *     console.log('Payment received:', data);
 *     queryClient.invalidateQueries(['payments']);
 *   });
 *   return unsubscribe;
 * }, [subscribe]);
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    autoReconnect = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 5,
    debug = false,
  } = options;

  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [isConnected, setIsConnected] = useState(wsManager.ws?.readyState === WebSocket.OPEN);

  const log = useCallback(
    (...args: unknown[]) => {
      if (debug) {
        console.log("[WebSocket]", ...args);
      }
    },
    [debug]
  );

  // Handle incoming messages - dispatches to all registered handlers
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        log("Received:", message.event, message.data);

        // Get handlers for this event type from the singleton manager
        const handlers = wsManager.handlers.get(message.event);
        if (handlers) {
          handlers.forEach((handler) => {
            try {
              handler(message.data, message);
            } catch (err) {
              if (import.meta.env.DEV) console.error("Handler error:", err);
            }
          });
        }

        // Also notify wildcard handlers
        const wildcardHandlers = wsManager.handlers.get("*");
        if (wildcardHandlers) {
          wildcardHandlers.forEach((handler) => {
            try {
              handler(message.data, message);
            } catch (err) {
              if (import.meta.env.DEV) console.error("Wildcard handler error:", err);
            }
          });
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error("Failed to parse WebSocket message:", err);
      }
    },
    [log]
  );

  // Connect to WebSocket (singleton - only creates connection if none exists)
  const connect = useCallback(() => {
    const accessToken = TokenStorage.getAccessToken();
    if (!isAuthenticated || !accessToken) {
      log("Not authenticated, skipping connection");
      return;
    }

    // Check if already connected via singleton
    if (wsManager.ws?.readyState === WebSocket.OPEN) {
      log("Already connected (singleton)");
      setIsConnected(true);
      return;
    }

    // Check if connection is in progress
    if (wsManager.isConnecting) {
      log("Connection already in progress");
      return;
    }

    wsManager.isConnecting = true;

    // ============================================================================
    // SECURITY NOTE: WebSocket Authentication via Query Parameter
    // ============================================================================
    // AWS API Gateway WebSocket API requires authentication at connection time.
    // Unlike HTTP requests, WebSocket connections cannot use Authorization headers
    // during the initial handshake - this is a limitation of the WebSocket protocol
    // and AWS API Gateway.
    //
    // The $connect route in API Gateway receives the token via query string and
    // validates it before allowing the connection. This is the standard pattern
    // for AWS API Gateway WebSocket APIs.
    //
    // Security mitigations:
    // 1. Access tokens are short-lived (8 hours) - limits exposure window
    // 2. WebSocket URL uses WSS (TLS encrypted) - token not visible in transit
    // 3. Server-side logs should NOT log full WebSocket URLs with tokens
    // 4. Browser history typically doesn't store WebSocket URLs
    //
    // Alternative considered but not used:
    // - Sec-WebSocket-Protocol header: Not supported by API Gateway
    // - Post-connection auth message: Adds latency and complexity
    // ============================================================================
    const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(accessToken)}`;
    log("Connecting to:", getWebSocketUrl()); // Note: Intentionally not logging full URL with token

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        log("Socket opened, connection established (singleton)");
        wsManager.isConnecting = false;
        wsManager.reconnectAttempts = 0;
        setIsConnected(true);
        // Dispatch connection event to all instances
        window.dispatchEvent(new CustomEvent("ws-connected"));
      };

      ws.onclose = (event) => {
        log("Disconnected:", event.code, event.reason);
        wsManager.isConnecting = false;
        wsManager.ws = null;
        setIsConnected(false);
        // Dispatch disconnection event to all instances
        window.dispatchEvent(new CustomEvent("ws-disconnected"));

        // Attempt reconnection
        if (autoReconnect && wsManager.reconnectAttempts < maxReconnectAttempts) {
          wsManager.reconnectAttempts++;
          log(`Reconnecting in ${reconnectDelay}ms (attempt ${wsManager.reconnectAttempts})`);
          wsManager.reconnectTimeout = setTimeout(connect, reconnectDelay);
        }
      };

      ws.onerror = (error) => {
        if (import.meta.env.DEV) console.error("WebSocket error:", error);
      };

      ws.onmessage = handleMessage;

      wsManager.ws = ws;
    } catch (err) {
      wsManager.isConnecting = false;
      if (import.meta.env.DEV) console.error("Failed to create WebSocket:", err);
    }
  }, [isAuthenticated, autoReconnect, reconnectDelay, maxReconnectAttempts, handleMessage, log]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    // Only actually disconnect if this is the last instance using it
    wsManager.connectionCount--;

    if (wsManager.connectionCount <= 0) {
      if (wsManager.reconnectTimeout) {
        clearTimeout(wsManager.reconnectTimeout);
        wsManager.reconnectTimeout = null;
      }

      if (wsManager.ws) {
        wsManager.ws.close();
        wsManager.ws = null;
      }

      wsManager.reconnectAttempts = maxReconnectAttempts; // Prevent auto-reconnect
      wsManager.connectionCount = 0;
    }

    setIsConnected(false);
  }, [maxReconnectAttempts]);

  // Subscribe to an event type (uses singleton handler map)
  const subscribe = useCallback(
    <T = unknown>(event: WebSocketEvent | string, handler: EventHandler<T>): (() => void) => {
      if (!wsManager.handlers.has(event)) {
        wsManager.handlers.set(event, new Set());
      }

      const handlers = wsManager.handlers.get(event)!;
      handlers.add(handler as EventHandler);

      log("Subscribed to:", event);

      // Return unsubscribe function
      return () => {
        handlers.delete(handler as EventHandler);
        if (handlers.size === 0) {
          wsManager.handlers.delete(event);
        }
        log("Unsubscribed from:", event);
      };
    },
    [log]
  );

  // Send a message
  const send = useCallback(
    (message: Record<string, unknown>) => {
      if (wsManager.ws?.readyState === WebSocket.OPEN) {
        wsManager.ws.send(JSON.stringify(message));
        log("Sent:", message);
      } else {
        if (import.meta.env.DEV) console.warn("WebSocket not connected, cannot send message");
      }
    },
    [log]
  );

  // Listen for connection state changes from other instances
  useEffect(() => {
    const handleConnected = () => setIsConnected(true);
    const handleDisconnected = () => setIsConnected(false);

    window.addEventListener("ws-connected", handleConnected);
    window.addEventListener("ws-disconnected", handleDisconnected);

    return () => {
      window.removeEventListener("ws-connected", handleConnected);
      window.removeEventListener("ws-disconnected", handleDisconnected);
    };
  }, []);

  // Auto-connect when authenticated, track connection count
  useEffect(() => {
    wsManager.connectionCount++;

    if (isAuthenticated) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect]);

  // Set up default handlers for cache invalidation
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Invalidate relevant queries when data changes (generic sync)
    unsubscribers.push(
      subscribe(WebSocketEvent.DATA_SYNC, (data: { entity_type: string; entity_id: string; action: string }) => {
        const { entity_type, action } = data;

        // Invalidate queries based on entity type
        const queryKeyMap: Record<string, string[]> = {
          student: ["students"],
          family: ["families"],
          payment: ["payments", "invoices"],
          invoice: ["invoices", "families"],
          enrollment: ["enrollments", "classes", "students"],
          class: ["classes"],
          attendance: ["attendance", "sessions"],
          application: ["applications"],
          discount: ["discounts"],
        };

        const keysToInvalidate = queryKeyMap[entity_type] || [entity_type];
        keysToInvalidate.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });

        log(`Cache invalidated for ${entity_type} (${action})`);
      })
    );

    // Invalidate specific queries for real-time events
    unsubscribers.push(
      subscribe(WebSocketEvent.APPLICATION_SUBMITTED, () => {
        queryClient.invalidateQueries({ queryKey: ["applications"] });
        log("Applications cache invalidated (new application)");
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.APPLICATION_APPROVED, () => {
        queryClient.invalidateQueries({ queryKey: ["applications"] });
        queryClient.invalidateQueries({ queryKey: ["enrollments"] });
        queryClient.invalidateQueries({ queryKey: ["students"] });
        log("Applications/enrollments cache invalidated (application approved)");
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.APPLICATION_DENIED, () => {
        queryClient.invalidateQueries({ queryKey: ["applications"] });
        log("Applications cache invalidated (application denied)");
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.PAYMENT_RECEIVED, () => {
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        log("Payments/invoices cache invalidated (payment received)");
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.INVOICE_GENERATED, () => {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        log("Invoices cache invalidated (invoice generated)");
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.ATTENDANCE_RECORDED, () => {
        queryClient.invalidateQueries({ queryKey: ["attendance"] });
        queryClient.invalidateQueries({ queryKey: ["sessions"] });
        log("Attendance cache invalidated (attendance recorded)");
      })
    );

    // Academic real-time events (Phase 1B / 2 / 3). Backend scopes the
    // audience to the student's guardians + tenant admins, so any handler
    // here only fires for users who legitimately should see the update.
    unsubscribers.push(
      subscribe(
        WebSocketEvent.GRADE_POSTED,
        (data: { student_id: string; assignment_id: string; class_id: string }) => {
          // Refresh anything that displays a grade for this student or class.
          queryClient.invalidateQueries({ queryKey: ["assignments"] });
          queryClient.invalidateQueries({
            queryKey: ["students", data.student_id],
          });
          queryClient.invalidateQueries({
            queryKey: ["classes", data.class_id, "gradebook"],
          });
          log(
            `Gradebook caches invalidated (grade posted for student ${data.student_id})`
          );
        }
      )
    );

    unsubscribers.push(
      subscribe(
        WebSocketEvent.BEHAVIOR_LOGGED,
        (data: { student_id: string }) => {
          queryClient.invalidateQueries({ queryKey: ["behavior"] });
          queryClient.invalidateQueries({
            queryKey: ["students", data.student_id],
          });
          log(`Behavior caches invalidated (incident logged for ${data.student_id})`);
        }
      )
    );

    unsubscribers.push(
      subscribe(
        WebSocketEvent.REPORT_CARD_GENERATED,
        (data: { student_id: string; card_id: string }) => {
          queryClient.invalidateQueries({ queryKey: ["reportCards"] });
          queryClient.invalidateQueries({
            queryKey: ["students", data.student_id],
          });
          log(`Report cards cache invalidated (card ${data.card_id})`);
        }
      )
    );

    unsubscribers.push(
      subscribe(
        WebSocketEvent.MESSAGE_NEW,
        (data: { thread_id: string }) => {
          // Inbox badge + thread view both need refresh.
          queryClient.invalidateQueries({ queryKey: ["messaging"] });
          queryClient.invalidateQueries({
            queryKey: ["messaging", "messages", data.thread_id],
          });
          log(`Messaging caches invalidated (new message in ${data.thread_id})`);
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [subscribe, queryClient, log]);

  return {
    isConnected,
    connect,
    disconnect,
    subscribe,
    send,
  };
}

/**
 * Hook for displaying real-time toast notifications
 *
 * Automatically shows toast messages for important events.
 */
export function useWebSocketNotifications() {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Payment notifications
    unsubscribers.push(
      subscribe(WebSocketEvent.PAYMENT_RECEIVED, (data: { family_name: string; amount: number }) => {
        showSuccess(`Payment of $${Number(data.amount).toFixed(2)} received from ${data.family_name}`);
      })
    );

    unsubscribers.push(
      subscribe(WebSocketEvent.PAYMENT_FAILED, (data: { family_name: string; error: string }) => {
        showError(`Payment failed for ${data.family_name}: ${data.error}`);
      })
    );

    // Application notifications
    unsubscribers.push(
      subscribe(WebSocketEvent.APPLICATION_SUBMITTED, (data: { student_name: string }) => {
        showInfo(`New application submitted for ${data.student_name}`);
      })
    );

    // Attendance notifications
    unsubscribers.push(
      subscribe(WebSocketEvent.ATTENDANCE_RECORDED, (data: { class_name: string; student_count: number }) => {
        showInfo(`Attendance recorded for ${data.class_name} (${data.student_count} students)`);
      })
    );

    // Discount approval notifications
    unsubscribers.push(
      subscribe(WebSocketEvent.DISCOUNT_PENDING_APPROVAL, (data: { student_name: string; discount_name: string }) => {
        showInfo(`Discount "${data.discount_name}" pending approval for ${data.student_name}`);
      })
    );

    // System announcements
    unsubscribers.push(
      subscribe(WebSocketEvent.SYSTEM_ANNOUNCEMENT, (data: { message: string }) => {
        showInfo(data.message);
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [subscribe]);
}

export default useWebSocket;
